import { AxiosRequestConfig } from "axios";
import { httpService } from "./HttpServices";
import { ApiResponse } from "./UserService";

export interface Customer {
  id: string;
  globalId: string;
  name: string;
  email: string;
  hubspotCompanyId?: string;
  hubspotContactId?: string;
  stripeCustomerId?: string;
  productUserId?: string;
  status: "active" | "pending" | "inactive";
  metadata: {
    phone?: string;
    address?: string;
    domain?: string;
    [key: string]: any;
  };
  createdAt: string;
  updatedAt: string;
}

export interface HubSpotCompany {
  id: string;
  name: string;
  domain?: string;
  hasDeals: boolean;
  closedWonDealsCount: number;
  totalDealsCount: number;
  alreadyInSystem: boolean;
  globalCustomerId?: string;
}

export interface CreateCustomerRequest {
  name: string;
  email: string;
  hubspotCompanyId?: string;
  hubspotContactId?: string;
  stripeCustomerId?: string;
  productUserId?: string;
  status?: "active" | "pending" | "inactive";
  metadata?: { [key: string]: any };
}

export interface UpdateCustomerRequest extends Partial<CreateCustomerRequest> {}

export interface CustomersResponse {
  customers: Customer[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export interface HubSpotCompaniesResponse {
  companies: HubSpotCompany[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    nextAfter?: string;
  };
  configMissing?: boolean;
  missingConfig?: {
    pipeline: boolean;
    stage: boolean;
  };
}

export interface DealProcessingResult {
  processed: number;
  skipped: number;
  errors: Array<{ dealId: string; error: string }>;
  contracts: Array<{ id: string; contractName: string }>;
}

export interface CreateCustomerResponse {
  customer: Customer;
  dealProcessing: DealProcessingResult | null;
}

export class CustomerService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = "/customers";
  }

  async getCustomers(
    params?: {
      page?: number;
      limit?: number;
      status?: "active" | "pending" | "inactive";
      search?: string;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
    },
    config?: AxiosRequestConfig,
  ): Promise<{ data: CustomersResponse; message: string }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append("page", String(params.page));
      if (params?.limit) queryParams.append("limit", String(params.limit));
      if (params?.status) queryParams.append("status", params.status);
      if (params?.search) queryParams.append("search", params.search);
      if (params?.sortBy) queryParams.append("sortBy", params.sortBy);
      if (params?.sortOrder) queryParams.append("sortOrder", params.sortOrder);

      const response = await httpService.get<ApiResponse<CustomersResponse>>(
        `${this.baseUrl}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`,
        config,
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to retrieve customers.";
      throw new Error(errorMessage);
    }
  }

  async getCustomer(
    id: string,
    config?: AxiosRequestConfig,
  ): Promise<{ data: Customer; message: string }> {
    try {
      const response = await httpService.get<
        ApiResponse<{ customer: Customer }>
      >(`${this.baseUrl}/${id}`, config);
      return {
        data: response.data.customer,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to retrieve customer.";
      throw new Error(errorMessage);
    }
  }

  async createCustomer(
    data: CreateCustomerRequest,
    config?: AxiosRequestConfig,
  ): Promise<{ data: CreateCustomerResponse; message: string }> {
    try {
      const response = await httpService.post<
        ApiResponse<CreateCustomerResponse>
      >(this.baseUrl, data, config);
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to create customer.";
      throw new Error(errorMessage);
    }
  }

  async updateCustomer(
    id: string,
    data: UpdateCustomerRequest,
    config?: AxiosRequestConfig,
  ): Promise<{ data: Customer; message: string }> {
    try {
      const response = await httpService.put<
        ApiResponse<{ customer: Customer }>
      >(`${this.baseUrl}/${id}`, data, config);
      return {
        data: response.data.customer,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to update customer.";
      throw new Error(errorMessage);
    }
  }

  async getCustomerContracts(
    id: string,
    config?: AxiosRequestConfig,
  ): Promise<{ data: any[]; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<{ contracts: any[] }>>(
        `${this.baseUrl}/${id}/contracts`,
        config,
      );
      return {
        data: response.data.contracts,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to retrieve customer contracts.";
      throw new Error(errorMessage);
    }
  }

  async searchHubSpotCompanies(
    params?: {
      search?: string;
      hasDeals?: boolean;
      hasClosedWonDeals?: boolean;
      page?: number;
      limit?: number;
      after?: string;
    },
    config?: AxiosRequestConfig,
  ): Promise<{ data: HubSpotCompaniesResponse; message: string }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.search) queryParams.append("search", params.search);
      if (params?.hasDeals) queryParams.append("hasDeals", "true");
      if (params?.hasClosedWonDeals)
        queryParams.append("hasClosedWonDeals", "true");
      if (params?.page) queryParams.append("page", String(params.page));
      if (params?.limit) queryParams.append("limit", String(params.limit));
      if (params?.after) queryParams.append("after", params.after);

      const response = await httpService.get<
        ApiResponse<HubSpotCompaniesResponse>
      >(
        `${this.baseUrl}/hubspot/companies${
          queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`,
        config,
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to search HubSpot companies.";
      throw new Error(errorMessage);
    }
  }

  async getHubSpotCompanyDeals(
    companyId: string,
    stage?: string,
    config?: AxiosRequestConfig,
  ): Promise<{ data: any[]; message: string }> {
    try {
      const queryParams = new URLSearchParams();
      if (stage) queryParams.append("stage", stage);

      const response = await httpService.get<ApiResponse<{ deals: any[] }>>(
        `${this.baseUrl}/hubspot/companies/${companyId}/deals${
          queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`,
        config,
      );
      return {
        data: response.data.deals,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to retrieve HubSpot company deals.";
      throw new Error(errorMessage);
    }
  }
}

export const customerService = new CustomerService();
